import { getEnvironmentConfig, getRuntimeConfig } from "./runtime.ts";

const API_URL_STORAGE_KEY = "apiUrlOverride";

export type ApiUrlOption = {
  label: string;
  url: string;
};

const isBrowser = typeof window !== "undefined";
const runtimeConfig = getRuntimeConfig();
const environmentConfig = getEnvironmentConfig();

const getStoredOverride = (): string | null => {
  if (!isBrowser) {
    return null;
  }

  const storedValue = window.localStorage.getItem(API_URL_STORAGE_KEY);

  if (!storedValue) {
    return null;
  }

  return storedValue;
};

const attemptCreateOption = (
  url: string,
  label?: string | null,
): ApiUrlOption | null => {
  const trimmedUrl = url.trim();

  if (!trimmedUrl) {
    return null;
  }

  try {
    new URL(trimmedUrl);
  } catch (error) {
    return null;
  }

  const normalizedLabel = label?.trim();

  return {
    label:
      normalizedLabel && normalizedLabel.length > 0
        ? normalizedLabel
        : trimmedUrl,
    url: trimmedUrl,
  } satisfies ApiUrlOption;
};

const parseStringValue = (value: string): ApiUrlOption | null => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  const separators = ["|", "::"];

  for (const separator of separators) {
    const separatorIndex = trimmedValue.indexOf(separator);

    if (separatorIndex === -1) {
      continue;
    }

    const label = trimmedValue.slice(0, separatorIndex).trim();
    const urlCandidate = trimmedValue
      .slice(separatorIndex + separator.length)
      .trim();

    const option = attemptCreateOption(urlCandidate, label);

    if (option) {
      return option;
    }
  }

  return attemptCreateOption(trimmedValue);
};

const normalizeParsedOption = (option: unknown): ApiUrlOption | null => {
  if (typeof option === "string") {
    return parseStringValue(option);
  }

  if (option && typeof option === "object") {
    const candidate = option as { url?: unknown; label?: unknown };

    if (typeof candidate.url !== "string") {
      return null;
    }

    const optionFromUrl = attemptCreateOption(
      candidate.url,
      typeof candidate.label === "string" ? candidate.label : undefined,
    );

    if (optionFromUrl) {
      return optionFromUrl;
    }

    return null;
  }

  return null;
};

const parseDelimitedList = (rawOptions: string): ApiUrlOption[] =>
  rawOptions
    .split(/\s*[\n,]\s*/)
    .map(parseStringValue)
    .filter((option): option is ApiUrlOption => option !== null);

const parseApiUrlOptionsFromUnknown = (
  rawOptions: unknown,
): ApiUrlOption[] => {
  if (!rawOptions) {
    return [];
  }

  if (typeof rawOptions === "string") {
    const trimmedValue = rawOptions.trim();

    if (!trimmedValue) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmedValue);

      if (Array.isArray(parsed)) {
        return parsed
          .map(normalizeParsedOption)
          .filter((option): option is ApiUrlOption => option !== null);
      }
    } catch (error) {
      return parseDelimitedList(trimmedValue);
    }

    return [];
  }

  if (Array.isArray(rawOptions)) {
    return rawOptions
      .map(normalizeParsedOption)
      .filter((option): option is ApiUrlOption => option !== null);
  }

  return [];
};

const humanizeIdentifier = (identifier: string): string => {
  const trimmedIdentifier = identifier.trim();

  if (!trimmedIdentifier) {
    return "";
  }

  if (trimmedIdentifier === trimmedIdentifier.toUpperCase()) {
    return trimmedIdentifier;
  }

  return trimmedIdentifier
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const API_OPTIONS_PREFIX = "VITE_API_URL_OPTIONS__";

type MutableApiOptionGroup = { label?: string; url?: string; order: number };

const parsePrefixedApiUrlOptions = (): ApiUrlOption[] => {
  const envEntries = Object.entries(import.meta.env ?? {}) as Array<
    [string, unknown]
  >;
  const grouped = new Map<string, MutableApiOptionGroup>();
  let orderCounter = 0;

  envEntries.forEach(([key, rawValue]) => {
    if (!key.startsWith(API_OPTIONS_PREFIX) || typeof rawValue !== "string") {
      return;
    }

    const value = rawValue.trim();

    if (!value) {
      return;
    }

    const remainder = key.slice(API_OPTIONS_PREFIX.length);

    if (!remainder) {
      return;
    }

    const segments = remainder.split("__");
    const identifier = segments[0];

    if (!identifier) {
      return;
    }

    const existing = grouped.get(identifier);
    const group =
      existing ??
      (() => {
        const created: MutableApiOptionGroup = { order: orderCounter++ };
        grouped.set(identifier, created);
        return created;
      })();

    if (segments.length === 1) {
      const option = parseStringValue(value);

      if (option) {
        group.url = option.url;

        if (!group.label && option.label !== option.url) {
          group.label = option.label;
        }
      }

      return;
    }

    const field = segments[1]?.toLowerCase();

    if (field === "label") {
      group.label = value;
      return;
    }

    if (field === "url") {
      const option = parseStringValue(value);

      if (option) {
        group.url = option.url;
      }
    }
  });

  return Array.from(grouped.entries())
    .map(([identifier, { label, url, order }]) => {
      if (!url) {
        return null;
      }

      const resolvedLabel = label ?? humanizeIdentifier(identifier);
      const option = attemptCreateOption(
        url,
        resolvedLabel && resolvedLabel.length > 0 ? resolvedLabel : undefined,
      );

      if (!option) {
        return null;
      }

      return { option, order };
    })
    .filter(
      (entry): entry is { option: ApiUrlOption; order: number } =>
        entry !== null,
    )
    .sort((left, right) => left.order - right.order)
    .map((entry) => entry.option);
};

const parseApiUrlOptions = (): ApiUrlOption[] => {
  const parsedOptions: ApiUrlOption[] = [];

  parsedOptions.push(...parseApiUrlOptionsFromUnknown(runtimeConfig.apiUrlOptions));
  parsedOptions.push(
    ...parseApiUrlOptionsFromUnknown(environmentConfig.apiUrlOptions),
  );
  parsedOptions.push(
    ...parseApiUrlOptionsFromUnknown(import.meta.env.VITE_API_URL_OPTIONS),
  );

  parsedOptions.push(...parsePrefixedApiUrlOptions());

  return parsedOptions;
};

const resolveEnvironment = (): string | undefined => {
  const hostEnvironment = deriveEnvironmentFromHost();

  if (hostEnvironment) {
    return hostEnvironment;
  }

  const envEnvironment = environmentConfig.environment?.trim();

  if (envEnvironment) {
    return envEnvironment.toLowerCase();
  }

  const runtimeEnvironment = isHostedNonLocal()
    ? undefined
    : runtimeConfig.environment?.trim();

  if (runtimeEnvironment) {
    return runtimeEnvironment.toLowerCase();
  }

  const buildEnvironment = import.meta.env.VITE_ENVIRONMENT;

  return typeof buildEnvironment === "string"
    ? buildEnvironment.toLowerCase()
    : undefined;
};

const normalizeEnvironmentName = (
  environment?: string | null,
): string | undefined => {
  if (typeof environment !== "string") {
    return undefined;
  }

  const normalized = environment.trim().toLowerCase();

  return normalized.length > 0 ? normalized : undefined;
};

const isProductionEnvironmentName = (environment?: string | null) => {
  const normalized = normalizeEnvironmentName(environment);

  return normalized === "production" || normalized === "prod";
};

const deriveEnvironmentFromHost = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  const host = window.location?.hostname?.toLowerCase();

  if (!host || isLocalHost(host)) {
    return undefined;
  }

  const primarySegment = host.split(".")[0];

  if (!primarySegment) {
    return undefined;
  }

  const normalized = primarySegment.toLowerCase();

  if (normalized.includes("prod")) {
    return "production";
  }

  if (normalized.includes("stg") || normalized.includes("stage")) {
    return "staging";
  }

  if (normalized.includes("beta")) {
    return "beta";
  }

  if (normalized.includes("uat")) {
    return "uat";
  }

  if (normalized.includes("qa")) {
    return "qa";
  }

  if (normalized.includes("test")) {
    return "test";
  }

  if (normalized.includes("dev")) {
    return "dev";
  }

  return undefined;
};

const resolveApiUrlFromEnvironmentMap = (
  map?: Record<string, string>,
): string | undefined => {
  if (!map) {
    return undefined;
  }

  const environment = resolveEnvironment();

  if (!environment) {
    return undefined;
  }

  const normalizedEntries = Object.entries(map).map(([key, value]) => [
    key.toLowerCase(),
    value,
  ]);

  const match = normalizedEntries.find(
    ([key]) => key === environment || key === environment.toLowerCase(),
  );

  const url = match?.[1];

  if (!url) {
    return undefined;
  }

  const option = attemptCreateOption(url);

  return option?.url ? normalizeApiBaseUrl(option.url) : undefined;
};

const isLocalHost = (host: string | undefined) => {
  if (!host) {
    return false;
  }

  const normalized = host.toLowerCase();

  return (
    normalized === "localhost" ||
    normalized.startsWith("127.") ||
    normalized === "[::1]" ||
    normalized === "::1"
  );
};

const isHostedNonLocal = () =>
  typeof window !== "undefined" && !isLocalHost(window.location?.hostname);

const deriveApiUrlFromHost = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  const protocol = window.location?.protocol ?? "https:";
  const host = window.location?.hostname?.toLowerCase();

  if (!host || isLocalHost(host)) {
    return undefined;
  }

  const match = host.match(/^([^.]+?)v2(\..+)$/i);

  if (!match) {
    return undefined;
  }

  const [, baseSubdomain, domain] = match;

  return normalizeApiBaseUrl(`${protocol}//${baseSubdomain}${domain}:8443`);
};

const resolveApiUrlFromHostMap = (
  map?: Record<string, string>,
): string | undefined => {
  if (!map || typeof window === "undefined") {
    return undefined;
  }

  const host = window.location?.hostname?.toLowerCase();
  if (!host || isLocalHost(host)) {
    return undefined;
  }

  const entries = Object.entries(map).map(([key, value]) => [
    key.toLowerCase(),
    value,
  ]);

  const directMatch = entries.find(([key]) => key === host)?.[1];

  const wildcardMatch = entries.find(
    ([key]) => key.startsWith("*.") && host.endsWith(key.substring(1)),
  )?.[1];

  const url = directMatch ?? wildcardMatch;
  if (!url) {
    return undefined;
  }

  const option = attemptCreateOption(url);

  return option?.url ? normalizeApiBaseUrl(option.url) : undefined;
};

const normalizeApiBaseUrl = (candidate?: string): string => {
  const fallback = "/";

  if (!candidate) {
    return fallback;
  }

  const trimmed = candidate.trim();

  if (!trimmed) {
    return fallback;
  }

  const protocolHint =
    typeof window !== "undefined" && window.location?.protocol
      ? window.location.protocol
      : "https:";
  const hostHint =
    typeof window !== "undefined" && window.location?.hostname
      ? window.location.hostname
      : "localhost";

  const ensureProtocol = (value: string) => {
    const hasProtocol = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(value);

    if (hasProtocol) {
      return value;
    }

    if (value.startsWith("//")) {
      return `${protocolHint}${value}`;
    }

    if (value.startsWith(":")) {
      return `${protocolHint}//${hostHint}${value}`;
    }

    return `${protocolHint}//${value}`;
  };

  try {
    const parsed = new URL(ensureProtocol(trimmed));

    if (parsed.pathname === "/api" || parsed.pathname === "/api/") {
      parsed.pathname = "/";
    } else if (parsed.pathname && parsed.pathname !== "/") {
      parsed.pathname = parsed.pathname.endsWith("/")
        ? parsed.pathname
        : `${parsed.pathname}/`;
    } else {
      parsed.pathname = "/";
    }

    return parsed.toString();
  } catch (error) {
    return fallback;
  }
};

type ApiUrlResolution = { url: string | undefined; source: string };

const resolveDefaultApiUrlWithSource = (): ApiUrlResolution => {
  const hostDerivedUrl = deriveApiUrlFromHost();

  if (hostDerivedUrl) {
    const derivedFromHost =
      typeof window !== "undefined" && window.location?.hostname
        ? window.location.hostname
        : "(unknown host)";

    return {
      url: hostDerivedUrl,
      source: `derived from host (${derivedFromHost})`,
    } satisfies ApiUrlResolution;
  }

  const hostMappedEnvUrl = resolveApiUrlFromHostMap(
    environmentConfig.apiUrlsByHost,
  );

  if (hostMappedEnvUrl) {
    return {
      url: hostMappedEnvUrl,
      source: "window __ENV__ host map (apiUrlsByHost)",
    } satisfies ApiUrlResolution;
  }

  const envMappedEnvUrl = resolveApiUrlFromEnvironmentMap(
    environmentConfig.apiUrlsByEnvironment,
  );

  if (envMappedEnvUrl) {
    return {
      url: envMappedEnvUrl,
      source: "window __ENV__ environment map (apiUrlsByEnvironment)",
    } satisfies ApiUrlResolution;
  }

  const envUrl = environmentConfig.apiUrl;
  const envOption = envUrl ? attemptCreateOption(envUrl) : null;

  if (envOption?.url) {
    return {
      url: normalizeApiBaseUrl(envOption.url),
      source: "window __ENV__ apiUrl",
    } satisfies ApiUrlResolution;
  }

  const buildEnvMappedHostUrl = resolveApiUrlFromHostMap(
    import.meta.env.VITE_API_URLS_BY_HOST,
  );

  if (buildEnvMappedHostUrl) {
    return {
      url: buildEnvMappedHostUrl,
      source: "build env host map (VITE_API_URLS_BY_HOST)",
    } satisfies ApiUrlResolution;
  }

  const envMappedBuildUrl = resolveApiUrlFromEnvironmentMap(
    import.meta.env.VITE_API_URLS_BY_ENVIRONMENT,
  );

  if (envMappedBuildUrl) {
    return {
      url: envMappedBuildUrl,
      source: "build env environment map (VITE_API_URLS_BY_ENVIRONMENT)",
    } satisfies ApiUrlResolution;
  }

  const buildRuntimeUrl = import.meta.env.VITE_API_URL;
  const buildRuntimeOption =
    buildRuntimeUrl && typeof buildRuntimeUrl === "string"
      ? attemptCreateOption(buildRuntimeUrl)
      : null;

  if (buildRuntimeOption?.url) {
    return {
      url: normalizeApiBaseUrl(buildRuntimeOption.url),
      source: "build env apiUrl (VITE_API_URL)",
    } satisfies ApiUrlResolution;
  }

  const runtimeFallback = resolveRuntimeApiUrl();

  if (runtimeFallback) {
    return runtimeFallback;
  }

  return { url: undefined, source: "(not resolved)" } satisfies ApiUrlResolution;
};

const resolveRuntimeApiUrl = (): ApiUrlResolution | undefined => {
  if (isHostedNonLocal()) {
    return {
      url: undefined,
      source: "(runtime config ignored on hosted deployment)",
    } satisfies ApiUrlResolution;
  }

  const hostMappedRuntimeUrl = resolveApiUrlFromHostMap(
    runtimeConfig.apiUrlsByHost,
  );

  if (hostMappedRuntimeUrl) {
    return {
      url: hostMappedRuntimeUrl,
      source: "runtime host map (apiUrlsByHost)",
    } satisfies ApiUrlResolution;
  }

  const envMappedRuntimeUrl = resolveApiUrlFromEnvironmentMap(
    runtimeConfig.apiUrlsByEnvironment,
  );

  if (envMappedRuntimeUrl) {
    return {
      url: envMappedRuntimeUrl,
      source: "runtime environment map (apiUrlsByEnvironment)",
    } satisfies ApiUrlResolution;
  }

  const runtimeUrl = runtimeConfig.apiUrl;
  const runtimeOption = runtimeUrl ? attemptCreateOption(runtimeUrl) : null;

  if (runtimeOption?.url) {
    return {
      url: normalizeApiBaseUrl(runtimeOption.url),
      source: "runtime apiUrl",
    } satisfies ApiUrlResolution;
  }

  return undefined;
};

const resolveDefaultApiUrl = (): string | undefined =>
  resolveDefaultApiUrlWithSource().url;

const isLocalEnvironment = () => resolveEnvironment() === "local";

export const canOverrideApiUrl = () => isLocalEnvironment();

const buildOptionList = (): ApiUrlOption[] => {
  if (!isLocalEnvironment()) {
    return [];
  }

  const defaultUrl = resolveDefaultApiUrl();
  const parsedOptions = parseApiUrlOptions().filter(
    (option, index, self) =>
      self.findIndex((candidate) => candidate.url === option.url) === index,
  );

  if (!defaultUrl) {
    return parsedOptions;
  }

  const hasDefault = parsedOptions.some((option) => option.url === defaultUrl);

  if (hasDefault) {
    return parsedOptions;
  }

  return [
    {
      label: defaultUrl,
      url: defaultUrl,
    },
    ...parsedOptions,
  ];
};

const apiUrlOptions = buildOptionList();

const isOptionAllowed = (candidate?: string | null) =>
  !!candidate && apiUrlOptions.some((option) => option.url === candidate);

type DiagnosticsLoggingOptions = {
  environmentName?: string | null;
  diagnosticsEnabledFromApi?: boolean | null;
};

const shouldLogDiagnostics = ({
  diagnosticsEnabledFromApi,
  environmentName,
}: DiagnosticsLoggingOptions): boolean => {
  if (typeof diagnosticsEnabledFromApi === "boolean") {
    return diagnosticsEnabledFromApi;
  }

  if (isProductionEnvironmentName(environmentName)) {
    return false;
  }

  return !isProductionEnvironmentName(resolveEnvironment());
};

export const logApiEnvironmentDiagnostics = (
  options?: DiagnosticsLoggingOptions,
) => {
  if (!isBrowser || typeof console === "undefined") {
    return;
  }

  const allowLogging = shouldLogDiagnostics({
    diagnosticsEnabledFromApi: options?.diagnosticsEnabledFromApi,
    environmentName: options?.environmentName,
  });

  if (!allowLogging) {
    return;
  }

  const resolvedEnvironment = resolveEnvironment();
  const hostEnvironmentHint = deriveEnvironmentFromHost();
  const defaultApiUrl = resolveDefaultApiUrlWithSource();
  const apiBaseUrl = resolveApiBaseUrl();
  const apiUrlOverride = getApiUrlOverride();
  const apiOptions = getApiUrlOptions();

  const buildEnvSnapshot = {
    VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT,
    VITE_API_URL: import.meta.env.VITE_API_URL,
    VITE_API_URL_OPTIONS: import.meta.env.VITE_API_URL_OPTIONS,
  };

  const group =
    typeof console.groupCollapsed === "function"
      ? console.groupCollapsed
      : console.log;

  const groupEnd =
    typeof console.groupEnd === "function" ? console.groupEnd : () => undefined;

  group("[Ticketing UI v2] Environment diagnostics");
  console.info("Resolved environment", resolvedEnvironment ?? "(undefined)");
  console.info("API environmentName", options?.environmentName ?? "(undefined)");
  console.info("Host environment hint", hostEnvironmentHint ?? "(none)");
  console.info("Runtime config", runtimeConfig);
  console.info("Window __ENV__", environmentConfig);
  console.info("Build-time env (import.meta.env)", buildEnvSnapshot);
  console.info("API URL options", apiOptions);
  console.info("Default API URL", defaultApiUrl.url ?? "(undefined)");
  console.info("Default API URL source", defaultApiUrl.source);
  console.info("Stored override", apiUrlOverride ?? "(none)");
  console.info("Effective API base URL", apiBaseUrl ?? "(undefined)");
  groupEnd();
};

export const getDefaultApiUrl = (): string | undefined => resolveDefaultApiUrl();

export const resolveApiBaseUrl = (): string | undefined => {
  const defaultUrl = resolveDefaultApiUrl();

  if (!isLocalEnvironment()) {
    return defaultUrl;
  }

  const override = getStoredOverride();

  if (isOptionAllowed(override)) {
    return override as string;
  }

  return defaultUrl;
};

export const getApiUrlOptions = (): ApiUrlOption[] => apiUrlOptions;

export const getApiUrlOverride = (): string | null => {
  const override = getStoredOverride();

  if (isOptionAllowed(override)) {
    return override;
  }

  return null;
};

export const setApiUrlOverride = (value: string | null) => {
  if (!isBrowser) {
    return;
  }

  if (!value) {
    window.localStorage.removeItem(API_URL_STORAGE_KEY);
    return;
  }

  if (!isOptionAllowed(value)) {
    return;
  }

  window.localStorage.setItem(API_URL_STORAGE_KEY, value);
};

export const resetApiUrlOverride = () => {
  if (!isBrowser) {
    return;
  }

  window.localStorage.removeItem(API_URL_STORAGE_KEY);
};
